<script setup>
// İade/Değişim Merkezi (Satış > İade/Değişim) — Emrah onaylı akış:
// Sevgi kod verir → müşteriye WhatsApp → paket gelir (Şevval eşler) →
// iade (IBAN → Emrah'a WP → "Ödemeyi Yaptım" → müşteriye WP) veya değişim.
// 30 gün gelmeyen kod otomatik kapanır. Stok/Prapazar'a dokunulmaz.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';
import ReturnOrderPicker from 'dashboard/components-next/Returns/ReturnOrderPicker.vue';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(false);
const error = ref('');
const info = ref('');
const requests = ref([]);
const counts = ref({});
const statusFilter = ref('open');
const query = ref('');
const expandedId = ref(null);
const busyId = ref(null);

// --- oluşturma formu ---
const showCreate = ref(false);
const form = ref({
  kind: 'product',
  refund_method: 'iyzico',
  marketplace_order_id: null,
  order_number: '',
  channel: 'ticimax',
  customer_name: '',
  phone: '',
  amount: '',
  item_summary: '',
  reason: '',
  fault: 'customer',
  resolution: '',
  notes: '',
});
const creating = ref(false);

// --- iade sebepleri: veritabanındaki 1000+ geçmiş kaydın gruplanmış hali ---
// (en sık: büyük geldi %34, küçük geldi %31, beğenmedi, yanlış/kusurlu ürün)
const REASONS_PRODUCT = [
  'Beden büyük geldi',
  'Beden küçük geldi',
  'Kalıp/beden sorunu',
  'Kalitesini/modelini beğenmedi',
  'Rengini beğenmedi',
  'Farklı ürün/renk ile değişim istiyor',
  'Yanlış ürün/renk/beden gönderildi (bizim hata)',
  'Kusurlu ürün gönderildi (bizim hata)',
  'Eksik ürün gönderildi (bizim hata)',
  'Vazgeçti',
  'Diğer',
];
const REASONS_PAYMENT = [
  'Fazla tahsilat',
  'Ürün stokta yok',
  'Çift kargo / çift tahsilat',
  'Sipariş iptali',
  'Diğer',
];
const reasonChoice = ref('');
const reasonCustom = ref('');
const reasonOptions = computed(() =>
  form.value.kind === 'payment' ? REASONS_PAYMENT : REASONS_PRODUCT
);
const onReasonChange = () => {
  // "bizim hata" sebepleri kargo cari kodunu da doğru tarafa çevirir
  if (reasonChoice.value.includes('bizim hata')) form.value.fault = 'company';
};

// --- telefon: girişte "5xx xxx xx xx" maskesi, görünümde "05xx xxx xx xx" ---
const fmtPhoneTr = p => {
  const d = String(p || '')
    .replace(/\D/g, '')
    .replace(/^90/, '')
    .replace(/^0+/, '');
  if (!/^5\d{9}$/.test(d)) return p;
  return `0${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8)}`;
};

const maskPhone = raw => {
  const d = String(raw || '')
    .replace(/\D/g, '')
    .replace(/^90/, '')
    .replace(/^0+/, '')
    .slice(0, 10);
  return [d.slice(0, 3), d.slice(3, 6), d.slice(6, 8), d.slice(8, 10)]
    .filter(Boolean)
    .join(' ');
};

const onPhoneInput = () => {
  form.value.phone = maskPhone(form.value.phone);
};

// --- aksiyon giriş alanları (satır bazlı) ---
const ibanInput = ref('');
const ibanHolderInput = ref('');
const exchangeNoInput = ref('');
const amountEdit = ref('');

const STATUS_META = {
  code_issued: { label: 'Kod Verildi', cls: 'bg-yellow-100 text-yellow-900' },
  received: { label: 'Paket Geldi', cls: 'bg-violet-100 text-violet-900' },
  awaiting_iban: { label: 'IBAN Bekleniyor', cls: 'bg-woot-75 text-woot-700' },
  refund_pending: { label: 'Ödeme Bekliyor', cls: 'bg-ruby-100 text-ruby-900' },
  exchange_pending: {
    label: 'Değişim Sürüyor',
    cls: 'bg-violet-100 text-violet-900',
  },
  refunded: { label: 'İade Ödendi', cls: 'bg-green-100 text-green-900' },
  exchanged: { label: 'Değişim Bitti', cls: 'bg-green-100 text-green-900' },
  expired: { label: 'Süresi Doldu', cls: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'İptal', cls: 'bg-slate-100 text-slate-700' },
};

const TABS = [
  { key: 'open', label: 'Açık Talepler' },
  { key: 'code_issued', label: 'Paket Bekleyen' },
  { key: 'received', label: 'Karar Bekleyen' },
  { key: 'awaiting_iban', label: 'IBAN Bekleyen' },
  { key: 'refund_pending', label: 'Ödeme Bekleyen' },
  { key: 'exchange_pending', label: 'Değişim' },
  { key: 'refunded', label: 'Ödenenler' },
  { key: 'exchanged', label: 'Değişenler' },
  { key: 'expired', label: 'Süresi Dolan' },
  { key: 'all', label: 'Tümü' },
];

const EVENT_LABELS = {
  code_issued: 'Kod verildi',
  received: 'Paket teslim alındı',
  decided: 'Tercih kaydedildi',
  iban_set: 'IBAN alındı',
  refunded: 'İade ödendi',
  exchanged: 'Değişim tamamlandı',
  cancelled: 'İptal edildi',
  expired: 'Süresi doldu',
  reopened: 'Tekrar açıldı',
  updated: 'Bilgiler güncellendi',
  wa_sent: '📱 Müşteriye WhatsApp gitti',
  msg_sent: '💬 Konuşmaya yazıldı',
  wa_failed: '⚠️ Bildirim GÖNDERİLEMEDİ',
  wa_skipped: 'Bildirim atlandı',
  cargo_code_missing: '⚠️ Kargo cari kodu tanımsız',
  emrah_notified: "📱 Emrah'a bildirim gitti",
  emrah_notify_failed: "⚠️ Emrah'a bildirim GÖNDERİLEMEDİ",
  transfer_received: '💰 Değişim havalesi alındı',
  exchange_order_created: '🛒 Değişim siparişi oluşturuldu',
  payment_refund_opened: '💸 Para iadesi açıldı',
  switch_to_havale: '↔️ Havaleye çevrildi',
};

const tabCount = key => {
  if (key === 'open')
    return (
      (counts.value.code_issued || 0) +
      (counts.value.received || 0) +
      (counts.value.awaiting_iban || 0) +
      (counts.value.refund_pending || 0) +
      (counts.value.exchange_pending || 0)
    );
  if (key === 'all')
    return Object.values(counts.value).reduce((a, b) => a + b, 0);
  return counts.value[key] || 0;
};

const fmtTl = v =>
  v == null
    ? '—'
    : `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(v)} TL`;

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams({ status: statusFilter.value });
    if (query.value.trim()) params.set('q', query.value.trim());
    const data = await fetchMarketplaceJson(
      accountId,
      `returns?${params.toString()}`
    );
    requests.value = data.requests || [];
    counts.value = data.counts || {};
  } catch (e) {
    error.value = `Liste yüklenemedi: ${e.message}`;
  } finally {
    loading.value = false;
  }
};

const setTab = key => {
  statusFilter.value = key;
  expandedId.value = null;
  load();
};

const toggleExpand = r => {
  if (expandedId.value === r.id) {
    expandedId.value = null;
    return;
  }
  expandedId.value = r.id;
  ibanInput.value = r.iban || '';
  ibanHolderInput.value = r.ibanHolder || '';
  exchangeNoInput.value = r.exchangeOrderNo || '';
  amountEdit.value = r.amount ?? '';
};

const replaceInList = updated => {
  const idx = requests.value.findIndex(r => r.id === updated.id);
  if (idx >= 0) requests.value[idx] = updated;
};

const fireEvent = async (r, event, payload = {}, confirmText = null) => {
  if (confirmText && !window.confirm(confirmText)) return;
  busyId.value = r.id;
  error.value = '';
  info.value = '';
  try {
    const updated = await postMarketplaceJsonBody(
      accountId,
      `returns/${r.id}/event`,
      { event, ...payload }
    );
    replaceInList(updated);
    info.value = `${updated.code}: ${updated.statusLabel}`;
    load();
  } catch (e) {
    error.value = e.message;
  } finally {
    busyId.value = null;
  }
};

const saveAmount = async r => {
  busyId.value = r.id;
  try {
    const updated = await patchMarketplaceJson(accountId, `returns/${r.id}`, {
      amount: amountEdit.value,
    });
    replaceInList(updated);
    info.value = 'Tutar güncellendi';
  } catch (e) {
    error.value = e.message;
  } finally {
    busyId.value = null;
  }
};

const cancelWithNote = r => {
  // eslint-disable-next-line no-alert
  const note = window.prompt('İptal sebebi (müşteriye mesaj gitmez):', '');
  if (note === null) return;
  fireEvent(r, 'cancel', { note });
};

// --- oluşturma: sipariş+kalem seçimi ortak bileşende (ReturnOrderPicker) ---
const picker = ref(null);
const selection = ref({ order: null, items: [], total: 0 });
const initialLookup = String(route.query.siparis || route.query.tel || '');

const onSelection = sel => {
  selection.value = sel;
  const o = sel.order;
  if (o) {
    form.value = {
      ...form.value,
      marketplace_order_id: o.id,
      order_number: o.orderNumber,
      channel: o.channel,
      customer_name: o.customerName || '',
      phone: maskPhone(o.phone),
    };
  }
  if (sel.items.length) form.value.amount = sel.total.toFixed(2);
};

const resetForm = () => {
  form.value = {
    kind: 'product',
    refund_method: 'iyzico',
    marketplace_order_id: null,
    order_number: '',
    channel: 'ticimax',
    customer_name: '',
    phone: '',
    amount: '',
    item_summary: '',
    reason: '',
    fault: 'customer',
    resolution: '',
    notes: '',
  };
  selection.value = { order: null, items: [], total: 0 };
  reasonChoice.value = '';
  reasonCustom.value = '';
  picker.value?.reset();
};

const createRequest = async () => {
  if (!form.value.customer_name && !form.value.phone) {
    error.value = 'En az müşteri adı veya telefon girin';
    return;
  }
  if (form.value.kind === 'payment' && !(parseFloat(form.value.amount) > 0)) {
    error.value = 'Para iadesinde tutar zorunludur';
    return;
  }
  const phoneDigits = form.value.phone.replace(/\D/g, '');
  if (form.value.phone && !/^5\d{9}$/.test(phoneDigits)) {
    error.value = 'Telefon "5xx xxx xx xx" biçiminde 10 hane olmalı';
    return;
  }
  const reasonFinal =
    reasonChoice.value === 'Diğer' ? reasonCustom.value : reasonChoice.value;
  creating.value = true;
  error.value = '';
  try {
    const created = await postMarketplaceJsonBody(accountId, 'returns', {
      ...form.value,
      phone: phoneDigits,
      reason: reasonFinal || form.value.reason,
      items: selection.value.items,
    });
    info.value =
      created.kind === 'payment'
        ? `${created.code} para iadesi açıldı (${created.refundMethod === 'iyzico' ? 'İyzico — kart iadesi bildirimi gönderildi' : 'havale — müşteriden IBAN istendi'})`
        : `${created.code} kodu verildi — müşteriye WhatsApp gönderildi`;
    showCreate.value = false;
    resetForm();
    statusFilter.value = 'open';
    load();
  } catch (e) {
    error.value = e.message;
  } finally {
    creating.value = false;
  }
};

const transferReceived = r =>
  (r.timeline || []).some(t => t.event === 'transfer_received');

// --- değişim siparişi sihirbazı: sipariş paneli (panel-app) modal iframe'de,
// kanal iade kaydının kanalına kilitli açılır; panel siparişi oluşturunca
// postMessage ile sipariş no gelir → kayda işlenir, müşteriye bilgi gider.
const exchangeWizard = ref(null);

const wizardUrl = r => {
  const params = new URLSearchParams({
    exchange: '1',
    exchange_code: r.code,
    platform: r.channel === 'shopify' ? 'shopify' : 'ticimax',
    phone: r.phone || '',
    contact_name: r.customerName || '',
    v: Date.now(),
  });
  return `/panel-app/index.html?${params.toString()}`;
};

const openExchangeWizard = r => {
  exchangeWizard.value = r;
};

const closeExchangeWizard = () => {
  exchangeWizard.value = null;
};

const onWizardMessage = e => {
  const d = e.data || {};
  if (d.type !== 'exchange-order-created' || !d.orderNo) return;
  const r =
    requests.value.find(x => x.code === d.code) || exchangeWizard.value;
  if (!r) return;
  exchangeNoInput.value = d.orderNo;
  fireEvent(r, 'exchange_order_created', { exchange_order_no: d.orderNo });
  // panelin "sipariş oluştu" ekranı kısa süre görünsün, sonra kendiliğinden kapansın
  setTimeout(closeExchangeWizard, 2500);
};

onMounted(() => window.addEventListener('message', onWizardMessage));
onBeforeUnmount(() => window.removeEventListener('message', onWizardMessage));

const waBadge = r => {
  const failed = (r.timeline || []).find(
    t => t.event === 'wa_failed' || t.event === 'emrah_notify_failed'
  );
  return failed ? '⚠️ Bildirim sorunu — geçmişe bakın' : null;
};

onMounted(() => {
  // Kişi kartından "İade/Değişim Başlat" ile gelindiyse form açık ve önceden dolu gelir
  if (initialLookup) showCreate.value = true;
  load();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-6xl w-full mx-auto p-6 flex flex-col gap-4">
      <!-- Başlık -->
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">İade / Değişim</h1>
          <p class="text-base text-n-slate-11 mt-1">
            Kod ver → paket gelsin → iade (IBAN → Emrah öder) veya değişim.
            Paket gelmezse kod 7 günde kendiliğinden kapanır. Ürünsüz para
            iadesi için "Para İadesi" türünü seçin.
          </p>
        </div>
        <button
          class="px-5 py-3 rounded-xl bg-woot-600 text-white text-lg font-semibold hover:bg-woot-700"
          @click="
            showCreate = !showCreate;
            if (!showCreate) resetForm();
          "
        >
          {{ showCreate ? 'Formu Kapat' : '+ Yeni İade Kodu Ver' }}
        </button>
      </div>

      <div
        v-if="error"
        class="rounded-lg bg-ruby-100 text-ruby-900 px-4 py-3 text-base font-medium"
      >
        {{ error }}
      </div>
      <div
        v-if="info"
        class="rounded-lg bg-green-100 text-green-900 px-4 py-3 text-base font-medium"
      >
        {{ info }}
      </div>

      <!-- Yeni talep formu -->
      <div
        v-if="showCreate"
        class="rounded-xl border-2 border-woot-200 bg-n-solid-2 p-5 flex flex-col gap-4"
      >
        <h2 class="text-lg font-bold text-n-slate-12">
          {{
            form.kind === 'payment'
              ? 'Yeni Para İadesi (ürün dönüşü yok)'
              : 'Yeni İade/Değişim Kodu'
          }}
        </h2>

        <!-- talep türü -->
        <div class="flex flex-wrap gap-2">
          <button
            class="px-5 py-3 rounded-xl text-lg font-bold border-2"
            :class="
              form.kind === 'product'
                ? 'bg-woot-600 text-white border-woot-600'
                : 'bg-n-solid-1 text-n-slate-12 border-n-weak hover:bg-n-solid-3'
            "
            @click="
              form.kind = 'product';
              reasonChoice = '';
            "
          >
            📦 Ürün İadesi / Değişim
          </button>
          <button
            class="px-5 py-3 rounded-xl text-lg font-bold border-2"
            :class="
              form.kind === 'payment'
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-n-solid-1 text-n-slate-12 border-n-weak hover:bg-n-solid-3'
            "
            @click="
              form.kind = 'payment';
              reasonChoice = '';
            "
          >
            💸 Para İadesi (ürün dönüşü yok)
          </button>
        </div>
        <p
          v-if="form.kind === 'payment'"
          class="text-base text-n-slate-11 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2"
        >
          Fazla tahsilat, stokta olmayan ürün gibi durumlarda kullanılır. Paket
          beklenmez, kargo kodu gitmez. İyzico seçilirse iade müşterinin kartına
          yapılır; İyzico'da bakiye yoksa kayıttaki "Havaleye Çevir" düğmesiyle
          IBAN akışına geçilir.
        </p>

        <ReturnOrderPicker
          ref="picker"
          :account-id="accountId"
          :initial-query="initialLookup"
          @selection="onSelection"
        />

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label class="flex flex-col gap-1 text-base text-n-slate-11">
            Müşteri Adı
            <input
              v-model="form.customer_name"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-base text-n-slate-11">
            Telefon (WhatsApp için)
            <input
              v-model="form.phone"
              placeholder="5xx xxx xx xx"
              inputmode="numeric"
              maxlength="13"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12 font-mono"
              @input="onPhoneInput"
            />
          </label>
          <label class="flex flex-col gap-1 text-base text-n-slate-11">
            Kanal
            <select
              v-model="form.channel"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            >
              <option value="ticimax">Koza Butik</option>
              <option value="shopify">ModaXLarge</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-base text-n-slate-11">
            {{
              form.kind === 'payment'
                ? 'İade Tutarı (TL) — zorunlu'
                : 'İade Tutarı (TL)'
            }}
            <input
              v-model="form.amount"
              type="number"
              step="0.01"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            />
          </label>
          <label
            v-if="form.kind === 'payment'"
            class="flex flex-col gap-1 text-base text-n-slate-11"
          >
            İade Yöntemi
            <select
              v-model="form.refund_method"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            >
              <option value="iyzico">İyzico — kartına iade</option>
              <option value="havale">Havale — müşteriden IBAN istenir</option>
            </select>
          </label>
          <label
            v-if="form.kind === 'product'"
            class="flex flex-col gap-1 text-base text-n-slate-11 md:col-span-2"
          >
            Ürün(ler)
            <input
              v-model="form.item_summary"
              placeholder="örn. 5399 siyah XL elbise"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-base text-n-slate-11">
            Sebep
            <select
              v-model="reasonChoice"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
              @change="onReasonChange"
            >
              <option value="">— Sebep seçin —</option>
              <option v-for="opt in reasonOptions" :key="opt" :value="opt">
                {{ opt }}
              </option>
            </select>
          </label>
          <label
            v-if="reasonChoice === 'Diğer'"
            class="flex flex-col gap-1 text-base text-n-slate-11"
          >
            Sebep (elle yazın)
            <input
              v-model="reasonCustom"
              placeholder="kısaca yazın"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            />
          </label>
          <label
            v-if="form.kind === 'product'"
            class="flex flex-col gap-1 text-base text-n-slate-11"
          >
            Hata Kimde? (kargo kodunu belirler)
            <select
              v-model="form.fault"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            >
              <option value="customer">
                Müşteri kaynaklı — kargo müşteriye ait
              </option>
              <option value="company">Bizim hatamız — kargo bize ait</option>
            </select>
          </label>
          <label
            v-if="form.kind === 'product'"
            class="flex flex-col gap-1 text-base text-n-slate-11"
          >
            Müşteri Tercihi (biliniyorsa)
            <select
              v-model="form.resolution"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            >
              <option value="">Henüz bilinmiyor</option>
              <option value="refund">İade (para iadesi)</option>
              <option value="exchange">Değişim</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-base text-n-slate-11">
            Not
            <input
              v-model="form.notes"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2.5 text-base text-n-slate-12"
            />
          </label>
        </div>
        <button
          class="self-start px-6 py-3 rounded-xl bg-green-600 text-white text-lg font-bold hover:bg-green-700 disabled:opacity-50"
          :disabled="creating"
          @click="createRequest"
        >
          {{
            creating
              ? 'Kaydediliyor…'
              : form.kind === 'payment'
                ? '💸 Para İadesini Aç'
                : 'Kodu Ver ve WhatsApp Gönder'
          }}
        </button>
      </div>

      <!-- Sekmeler -->
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tab in TABS"
          :key="tab.key"
          class="px-4 py-2 rounded-full text-base font-semibold border"
          :class="
            statusFilter === tab.key
              ? 'bg-woot-600 text-white border-woot-600'
              : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-woot-400'
          "
          @click="setTab(tab.key)"
        >
          {{ tab.label }}
          <span class="ml-1 font-bold">{{ tabCount(tab.key) }}</span>
        </button>
      </div>

      <!-- Arama -->
      <div class="flex gap-2">
        <input
          v-model="query"
          type="text"
          placeholder="Ara: KZ-kodu, isim, telefon veya sipariş no"
          class="flex-1 rounded-lg border border-n-strong bg-n-solid-1 px-4 py-3 text-base text-n-slate-12"
          @keyup.enter="load"
        />
        <button
          class="px-5 py-3 rounded-lg bg-n-solid-3 text-n-slate-12 text-base font-semibold hover:bg-n-solid-1"
          @click="load"
        >
          Ara
        </button>
      </div>

      <!-- Liste -->
      <div v-if="loading" class="text-center text-lg text-n-slate-11 py-10">
        Yükleniyor…
      </div>
      <div
        v-else-if="!requests.length"
        class="text-center text-lg text-n-slate-11 py-10"
      >
        Bu görünümde kayıt yok.
      </div>
      <div v-else class="flex flex-col gap-3">
        <div
          v-for="r in requests"
          :key="r.id"
          class="rounded-xl border bg-n-solid-2"
          :class="
            r.status === 'refund_pending' ? 'border-ruby-300' : 'border-n-weak'
          "
        >
          <!-- satır başlığı -->
          <button
            class="w-full text-left px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-2"
            @click="toggleExpand(r)"
          >
            <span class="text-xl font-bold text-n-slate-12">{{ r.code }}</span>
            <span
              class="px-3 py-1 rounded-full text-sm font-bold"
              :class="STATUS_META[r.status]?.cls"
            >
              {{ STATUS_META[r.status]?.label || r.status }}
            </span>
            <span
              class="px-2 py-0.5 rounded-full text-xs font-bold"
              :class="
                r.channel === 'ticimax'
                  ? 'bg-violet-100 text-violet-800'
                  : 'bg-green-100 text-green-800'
              "
            >
              {{ r.channelLabel }}
            </span>
            <span
              v-if="r.kind === 'payment'"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800"
            >
              💸 Para İadesi ·
              {{ r.refundMethod === 'iyzico' ? 'İyzico' : 'Havale' }}
            </span>
            <span class="text-lg font-semibold text-n-slate-12">
              {{ r.customerName || '—' }}
            </span>
            <span class="text-base text-n-slate-11">{{ fmtTl(r.amount) }}</span>
            <span v-if="r.itemSummary" class="text-base text-n-slate-11">
              {{ r.itemSummary }}
            </span>
            <span
              v-if="r.status === 'code_issued' && r.daysLeft != null"
              class="text-sm font-semibold"
              :class="r.daysLeft <= 5 ? 'text-ruby-700' : 'text-n-slate-11'"
            >
              ⏳ {{ r.daysLeft }} gün kaldı
            </span>
            <span v-if="waBadge(r)" class="text-sm font-bold text-ruby-700">
              {{ waBadge(r) }}
            </span>
            <span class="ml-auto text-sm text-n-slate-10">{{
              r.createdAt
            }}</span>
          </button>

          <!-- detay -->
          <div
            v-if="expandedId === r.id"
            class="border-t border-n-weak px-5 py-4 flex flex-col gap-4"
          >
            <div
              class="flex flex-wrap gap-x-8 gap-y-2 text-base text-n-slate-11"
            >
              <span v-if="r.orderNumber">
                Sipariş: <b class="text-n-slate-12">#{{ r.orderNumber }}</b>
              </span>
              <span v-if="r.phone">
                Telefon:
                <b class="text-n-slate-12">{{ fmtPhoneTr(r.phone) }}</b>
              </span>
              <span v-if="r.faultLabel">
                Hata: <b class="text-n-slate-12">{{ r.faultLabel }}</b>
              </span>
              <span v-if="r.carrierLabel">
                Sipariş kargosu:
                <b class="text-n-slate-12">{{ r.carrierLabel }}</b>
              </span>
              <span v-if="r.reason">
                Sebep: <b class="text-n-slate-12">{{ r.reason }}</b>
              </span>
              <span v-if="r.resolution">
                Tercih:
                <b class="text-n-slate-12">
                  {{ r.resolution === 'refund' ? 'İADE' : 'DEĞİŞİM' }}
                </b>
              </span>
              <span v-if="r.iban">
                IBAN: <b class="text-n-slate-12">{{ r.iban }}</b>
                <template v-if="r.ibanHolder"> ({{ r.ibanHolder }})</template>
              </span>
              <span v-if="r.exchangeOrderNo">
                Yeni sipariş:
                <b class="text-n-slate-12">#{{ r.exchangeOrderNo }}</b>
              </span>
              <span v-if="r.createdBy">
                Açan: <b class="text-n-slate-12">{{ r.createdBy }}</b>
              </span>
              <span v-if="r.notes">
                Not: <b class="text-n-slate-12">{{ r.notes }}</b>
              </span>
            </div>

            <!-- iade edilen ürünler (görselli) -->
            <div v-if="r.items?.length" class="flex flex-wrap gap-3">
              <div
                v-for="(it, i) in r.items"
                :key="i"
                class="w-28 rounded-xl border border-n-weak overflow-hidden bg-n-solid-1"
              >
                <img
                  v-if="it.image"
                  :src="it.image"
                  class="w-full h-28 object-cover"
                  loading="lazy"
                />
                <div
                  v-else
                  class="w-full h-28 bg-n-alpha-black2 flex items-center justify-center text-2xl"
                >
                  👗
                </div>
                <div class="p-1.5">
                  <div class="text-xs font-bold text-n-slate-12 truncate">
                    {{ it.code }}
                  </div>
                  <div class="text-[11px] text-n-slate-11 truncate">
                    {{ it.variant }}
                  </div>
                  <div class="text-[11px] text-n-slate-11">
                    {{ it.quantity }} adet · {{ fmtTl(it.total) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- tutar düzenleme (açık kayıtlarda) -->
            <div v-if="r.open" class="flex items-center gap-2">
              <label class="text-base text-n-slate-11">İade tutarı:</label>
              <input
                v-model="amountEdit"
                type="number"
                step="0.01"
                class="w-36 rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
              />
              <button
                class="px-3 py-2 rounded-lg bg-n-solid-3 text-base font-semibold text-n-slate-12 hover:bg-n-solid-1"
                :disabled="busyId === r.id"
                @click="saveAmount(r)"
              >
                Kaydet
              </button>
            </div>

            <!-- durum aksiyonları -->
            <div class="flex flex-wrap gap-3">
              <template v-if="r.status === 'code_issued'">
                <button
                  class="px-5 py-3 rounded-xl bg-violet-600 text-white text-lg font-bold hover:bg-violet-700"
                  :disabled="busyId === r.id"
                  @click="fireEvent(r, 'receive')"
                >
                  📦 Paket Geldi
                </button>
                <button
                  v-if="!r.resolution"
                  class="px-4 py-3 rounded-xl bg-n-solid-3 text-n-slate-12 text-base font-semibold hover:bg-n-solid-1"
                  :disabled="busyId === r.id"
                  @click="fireEvent(r, 'decide', { resolution: 'refund' })"
                >
                  Tercih: İade
                </button>
                <button
                  v-if="!r.resolution"
                  class="px-4 py-3 rounded-xl bg-n-solid-3 text-n-slate-12 text-base font-semibold hover:bg-n-solid-1"
                  :disabled="busyId === r.id"
                  @click="fireEvent(r, 'decide', { resolution: 'exchange' })"
                >
                  Tercih: Değişim
                </button>
              </template>

              <template v-if="r.status === 'received'">
                <button
                  class="px-5 py-3 rounded-xl bg-woot-600 text-white text-lg font-bold hover:bg-woot-700"
                  :disabled="busyId === r.id"
                  @click="fireEvent(r, 'decide', { resolution: 'refund' })"
                >
                  💸 İade Seçildi
                </button>
                <button
                  class="px-5 py-3 rounded-xl bg-violet-600 text-white text-lg font-bold hover:bg-violet-700"
                  :disabled="busyId === r.id"
                  @click="fireEvent(r, 'decide', { resolution: 'exchange' })"
                >
                  🔄 Değişim Seçildi
                </button>
              </template>

              <template v-if="['received', 'awaiting_iban'].includes(r.status)">
                <div
                  class="flex flex-wrap items-center gap-2 rounded-xl border border-n-weak p-3 bg-n-solid-1"
                >
                  <input
                    v-model="ibanInput"
                    placeholder="TR.. IBAN (26 hane)"
                    class="w-80 rounded-lg border border-n-strong bg-n-solid-2 px-3 py-2.5 text-base text-n-slate-12 font-mono"
                  />
                  <input
                    v-model="ibanHolderInput"
                    placeholder="Hesap sahibi ad soyad"
                    class="w-56 rounded-lg border border-n-strong bg-n-solid-2 px-3 py-2.5 text-base text-n-slate-12"
                  />
                  <button
                    class="px-5 py-2.5 rounded-xl bg-woot-600 text-white text-base font-bold hover:bg-woot-700"
                    :disabled="busyId === r.id"
                    @click="
                      fireEvent(r, 'set_iban', {
                        iban: ibanInput,
                        iban_holder: ibanHolderInput,
                      })
                    "
                  >
                    IBAN Kaydet → Emrah'a Bildir
                  </button>
                </div>
              </template>

              <template v-if="r.status === 'refund_pending'">
                <button
                  class="px-6 py-3 rounded-xl bg-green-600 text-white text-lg font-bold hover:bg-green-700"
                  :disabled="busyId === r.id"
                  @click="
                    fireEvent(
                      r,
                      'refunded',
                      {},
                      r.kind === 'payment' && r.refundMethod === 'iyzico'
                        ? `${r.code} — ${fmtTl(r.amount)} İyzico kart iadesini yaptınız mı? Müşteriye 'iadeniz kartınıza yapıldı' mesajı gidecek.`
                        : `${r.code} — ${fmtTl(r.amount)} ödemesini yaptınız mı? Müşteriye 'iadeniz gerçekleşti' mesajı gidecek.`
                    )
                  "
                >
                  {{
                    r.kind === 'payment' && r.refundMethod === 'iyzico'
                      ? '✅ İyzico İadesini Yaptım'
                      : '✅ Ödemeyi Yaptım (Emrah)'
                  }}
                </button>
                <button
                  v-if="r.kind === 'payment' && r.refundMethod === 'iyzico'"
                  class="px-5 py-3 rounded-xl bg-yellow-600 text-white text-base font-bold hover:bg-yellow-700"
                  :disabled="busyId === r.id"
                  @click="
                    fireEvent(
                      r,
                      'switch_to_havale',
                      {},
                      `${r.code} — İyzico'da bakiye yok mu? Havaleye çevrilecek ve müşteriden IBAN istenecek.`
                    )
                  "
                >
                  ↔️ Havaleye Çevir (bakiye yok)
                </button>
              </template>

              <template v-if="r.status === 'exchange_pending'">
                <div
                  class="flex flex-col gap-3 w-full rounded-xl border border-n-weak p-3 bg-n-solid-1"
                >
                  <span
                    v-if="r.fault !== 'company'"
                    class="text-base text-n-slate-11"
                  >
                    Ücret: 110 TL şubede ödendi + <b>110 TL havale</b> bize
                    gelecek.
                  </span>
                  <div class="flex flex-wrap items-center gap-2">
                    <button
                      v-if="r.fault !== 'company' && !transferReceived(r)"
                      class="px-5 py-2.5 rounded-xl bg-yellow-600 text-white text-base font-bold hover:bg-yellow-700"
                      :disabled="busyId === r.id"
                      @click="fireEvent(r, 'transfer_received')"
                    >
                      💰 Havale Alındı (110 TL)
                    </button>
                    <span
                      v-else-if="r.fault !== 'company'"
                      class="px-3 py-2 rounded-lg bg-green-100 text-green-900 text-base font-semibold"
                    >
                      💰 Havale alındı ✓
                    </span>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      v-if="r.exchangeOrderNo"
                      class="px-3 py-2 rounded-lg bg-violet-100 text-violet-900 text-base font-bold"
                    >
                      🛒 Değişim siparişi: {{ r.exchangeOrderNo }}
                    </span>
                    <button
                      v-if="
                        !r.exchangeOrderNo &&
                        ['ticimax', 'shopify'].includes(r.channel)
                      "
                      class="px-5 py-3 rounded-xl bg-violet-600 text-white text-lg font-bold hover:bg-violet-700"
                      :disabled="busyId === r.id"
                      @click="openExchangeWizard(r)"
                    >
                      🛒 Değişim Siparişi Oluştur ({{ r.channelLabel }})
                    </button>
                    <input
                      v-model="exchangeNoInput"
                      :placeholder="
                        r.exchangeOrderNo
                          ? 'Sipariş no (düzeltmek için)'
                          : 'Ya da sipariş no elle girin'
                      "
                      class="w-56 rounded-lg border border-n-strong bg-n-solid-2 px-3 py-2.5 text-base text-n-slate-12"
                    />
                    <button
                      class="px-5 py-2.5 rounded-xl bg-green-600 text-white text-base font-bold hover:bg-green-700"
                      :disabled="busyId === r.id"
                      @click="
                        fireEvent(r, 'exchanged', {
                          exchange_order_no:
                            exchangeNoInput || r.exchangeOrderNo,
                        })
                      "
                    >
                      ✅ Değişimi Tamamla
                    </button>
                  </div>
                </div>
              </template>

              <button
                v-if="r.open"
                class="px-4 py-3 rounded-xl bg-n-solid-3 text-ruby-700 text-base font-semibold hover:bg-ruby-50"
                :disabled="busyId === r.id"
                @click="cancelWithNote(r)"
              >
                İptal Et
              </button>
              <button
                v-if="['expired', 'cancelled'].includes(r.status)"
                class="px-4 py-3 rounded-xl bg-n-solid-3 text-n-slate-12 text-base font-semibold hover:bg-n-solid-1"
                :disabled="busyId === r.id"
                @click="fireEvent(r, 'reopen')"
              >
                Tekrar Aç
              </button>
            </div>

            <!-- zaman çizelgesi -->
            <div class="flex flex-col gap-1.5">
              <h4 class="text-base font-bold text-n-slate-12">Geçmiş</h4>
              <div
                v-for="(t, i) in r.timeline"
                :key="i"
                class="flex gap-3 text-base"
              >
                <span class="text-n-slate-10 shrink-0 w-36">
                  {{
                    new Date(t.at).toLocaleString('tr-TR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })
                  }}
                </span>
                <span
                  class="font-semibold shrink-0"
                  :class="
                    t.event.includes('failed')
                      ? 'text-ruby-700'
                      : 'text-n-slate-12'
                  "
                >
                  {{ EVENT_LABELS[t.event] || t.event }}
                </span>
                <span class="text-n-slate-11">
                  {{ t.note }}
                  <template v-if="t.by"> — {{ t.by }}</template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Değişim siparişi sihirbazı: sipariş paneli, iade kanalına kilitli -->
    <div
      v-if="exchangeWizard"
      class="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3"
      @click.self="closeExchangeWizard"
    >
      <div
        class="bg-n-solid-2 rounded-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden shadow-2xl"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <span class="text-lg font-bold text-n-slate-12">
            🔄 {{ exchangeWizard.code }} — Değişim Siparişi ({{
              exchangeWizard.channelLabel
            }})
          </span>
          <button
            class="px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 text-base font-semibold hover:bg-n-solid-1"
            @click="closeExchangeWizard"
          >
            Kapat ✕
          </button>
        </div>
        <iframe
          :src="wizardUrl(exchangeWizard)"
          class="flex-1 w-full border-0 bg-white"
          title="Değişim siparişi paneli"
        />
      </div>
    </div>
  </div>
</template>
